import React, { useState, useEffect, useRef } from 'react';
import { changePassword } from '../services/api';

// PR-R3: Self-service change password modal.
//
// Opened from the Navbar by an authenticated user. Submits to
// PUT /participants/me/password via services/api#changePassword.
//
// Client-side validation:
//   - all three fields required
//   - new password >= 8 chars (matches backend zod rule)
//   - confirm matches new
//   - new !== current (matches backend handler check)
// Server-side errors (wrong current password, rate limit, etc.) are surfaced
// via err.response.data.error from the axios interceptor.
//
// Backdrop click and Escape close the modal. The first input autofocuses
// when the modal opens.

function ChangePasswordModal({ onClose }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword,     setNewPassword]     = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [error,     setError]     = useState('');
    const [success,   setSuccess]   = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const firstInputRef = useRef(null);

    // Autofocus the first field on open. Also wire up Escape-to-close --
    // only while the modal is mounted, so we clean up on unmount.
    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const validate = () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            return 'All fields are required.';
        }
        if (newPassword.length < 8) {
            return 'New password must be at least 8 characters.';
        }
        if (newPassword !== confirmPassword) {
            return 'New password and confirmation do not match.';
        }
        if (newPassword === currentPassword) {
            return 'New password must be different from current password.';
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);
        try {
            await changePassword(currentPassword, newPassword);
            setSuccess(true);
        } catch (err) {
            setError(
                err.response?.data?.error ||
                'Could not change password. Please try again.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Backdrop click closes the modal. stopPropagation on the inner container
    // prevents clicks inside the modal body from also closing it.
    const handleBackdropClick = () => {
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="Change password"
        >
            <div
                className="w-full max-w-md bg-gray-900 rounded-2xl p-8 border border-gray-800"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white">Change password</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white text-2xl leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {success ? (
                    <div>
                        <div className="bg-green-900/50 border border-green-500 text-green-300 px-4 py-3 rounded-lg mb-6 text-sm">
                            Password changed successfully.
                        </div>
                        <button
                            onClick={onClose}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                                {error}
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Current password
                                </label>
                                <input
                                    ref={firstInputRef}
                                    type="password"
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                    placeholder="••••••••"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    New password
                                </label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                    placeholder="At least 8 characters"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Confirm new password
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                    placeholder="••••••••"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    required
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={submitting}
                                    className="flex-1 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-900 text-white font-bold py-3 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 text-white font-bold py-3 rounded-lg transition-colors"
                                >
                                    {submitting ? 'Saving...' : 'Change password'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}

export default ChangePasswordModal;